import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Mail, Phone, MessageCircle, Globe, Send } from "lucide-react";
import api from "@/lib/api";
import { MaskLines, Reveal, Eyebrow } from "@/components/anim";
import { CONTACT, INTERESTS } from "@/lib/content";

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  organization: "",
  service_interest: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);
  const [params] = useSearchParams();

  useEffect(() => {
    const interest = params.get("interest");
    if (interest) setForm((f) => ({ ...f, service_interest: interest }));
  }, [params]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error("Please fill name, email, phone and message.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      setSuccess(data);
      toast.success("Mission logged. We'll get back to you shortly.");
    } catch (err) {
      console.error(err);
      toast.error("Could not send right now. Please email us directly.");
    } finally {
      setSending(false);
    }
  };

  const channels = [
    { icon: Mail, label: "EMAIL", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email-link" },
    { icon: Phone, label: "PHONE", value: CONTACT.phone, href: CONTACT.phoneHref, testid: "contact-phone-link" },
    { icon: MessageCircle, label: "WHATSAPP", value: "Chat with our team", href: CONTACT.whatsapp, testid: "contact-whatsapp-link" },
    { icon: Globe, label: "WEBSITE", value: CONTACT.website, href: `https://${CONTACT.website}`, testid: "contact-website-link" },
  ];

  const inputCls =
    "w-full rounded-xl border border-white/12 bg-panel/60 px-4 py-3.5 text-sm text-white placeholder-[#6B7280] outline-none transition-all duration-300 focus:border-kinetic focus:shadow-[0_0_0_3px_rgba(255,122,26,0.12)]";

  return (
    <main className="relative overflow-hidden">
      <section className="relative hud-grid noise pt-36 md:pt-44 pb-24 md:pb-32">
        <div className="pointer-events-none absolute -top-32 right-0 h-[440px] w-[440px] rounded-full bg-kinetic/10 blur-[130px]" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-8">
          <MaskLines
            lines={[
              <span key="e" className="font-hud text-[11px] md:text-xs uppercase tracking-[0.3em] text-kinetic">
                [ CONTACT ]
              </span>,
            ]}
          />
          <MaskLines
            className="mt-6 font-display font-extrabold tracking-[-0.02em] leading-[0.98] text-[clamp(2.4rem,5.6vw,5.2rem)]"
            lines={["OPEN A", <span key="2"><span className="text-kinetic">CHANNEL.</span></span>]}
            delay={0.2}
          />

          <div className="mt-14 grid gap-12 lg:grid-cols-[1.05fr_0.95fr]">
            <Reveal>
              {success ? (
                <div
                  className="flex h-full flex-col items-start justify-center rounded-2xl border border-kinetic/30 bg-kinetic/5 p-10 glow-cyan"
                  data-testid="contact-success-panel"
                >
                  <CheckCircle2 className="h-12 w-12 text-kinetic" />
                  <h3 className="mt-6 font-display text-2xl md:text-3xl font-bold">Message received.</h3>
                  <p className="mt-3 text-[#9CA3AF] leading-relaxed max-w-md">
                    Your enquiry is logged with reference{" "}
                    <span className="font-hud text-kinetic">{success.id || success._id}</span>. The Aeris team will
                    respond to {success.email} shortly.
                  </p>
                  <button
                    data-testid="contact-send-another-button"
                    onClick={() => {
                      setForm(INITIAL);
                      setSuccess(null);
                    }}
                    className="mt-8 inline-flex items-center gap-2 rounded-full border border-kinetic/50 px-6 py-3 font-hud text-[11px] font-semibold uppercase tracking-[0.18em] text-kinetic transition-all duration-300 hover:bg-kinetic hover:text-void"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block font-hud text-[10px] uppercase tracking-[0.22em] text-[#9CA3AF]" htmlFor="cf-name">NAME *</label>
                      <input id="cf-name" data-testid="contact-input-name" className={inputCls} placeholder="Your full name" value={form.name} onChange={set("name")} />
                    </div>
                    <div>
                      <label className="mb-2 block font-hud text-[10px] uppercase tracking-[0.22em] text-[#9CA3AF]" htmlFor="cf-org">ORGANISATION</label>
                      <input id="cf-org" data-testid="contact-input-organization" className={inputCls} placeholder="Company / institution" value={form.organization} onChange={set("organization")} />
                    </div>
                    <div>
                      <label className="mb-2 block font-hud text-[10px] uppercase tracking-[0.22em] text-[#9CA3AF]" htmlFor="cf-email">EMAIL *</label>
                      <input id="cf-email" data-testid="contact-input-email" type="email" className={inputCls} placeholder="you@example.com" value={form.email} onChange={set("email")} />
                    </div>
                    <div>
                      <label className="mb-2 block font-hud text-[10px] uppercase tracking-[0.22em] text-[#9CA3AF]" htmlFor="cf-phone">PHONE *</label>
                      <input id="cf-phone" data-testid="contact-input-phone" type="tel" className={inputCls} placeholder="+91 ..." value={form.phone} onChange={set("phone")} />
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 block font-hud text-[10px] uppercase tracking-[0.22em] text-[#9CA3AF]" htmlFor="cf-interest">I'M INTERESTED IN</label>
                    <div className="relative">
                      <select id="cf-interest" data-testid="contact-select-interest" className={`${inputCls} appearance-none pr-10`} value={form.service_interest} onChange={set("service_interest")}>
                        <option value="">Select a service…</option>
                        {INTERESTS.map((i) => (
                          <option key={i} value={i} className="bg-panel">{i}</option>
                        ))}
                      </select>
                      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#6B7280]">▾</span>
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 block font-hud text-[10px] uppercase tracking-[0.22em] text-[#9CA3AF]" htmlFor="cf-message">MESSAGE *</label>
                    <textarea id="cf-message" data-testid="contact-input-message" rows="5" className={`${inputCls} resize-none`} placeholder="Tell us about your mission — crops, sites, timelines…" value={form.message} onChange={set("message")} />
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-form-submit-button"
                    className="group inline-flex items-center gap-3 rounded-full bg-kinetic px-8 py-4 font-hud text-xs font-semibold uppercase tracking-[0.18em] text-void transition-all duration-300 hover:shadow-[0_0_44px_rgba(255,122,26,0.5)] hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Transmitting…
                      </>
                    ) : (
                      <>
                        Send message <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </Reveal>

            <div className="space-y-4">
              {channels.map((c, i) => (
                <Reveal key={c.label} delay={i * 0.07}>
                  <a
                    href={c.href}
                    target={c.label === "WHATSAPP" || c.label === "WEBSITE" ? "_blank" : undefined}
                    rel="noreferrer"
                    data-testid={c.testid}
                    className="group flex items-center gap-5 rounded-2xl border border-white/10 bg-panel/50 p-6 transition-all duration-300 hover:border-kinetic/50 hover:shadow-[0_0_32px_rgba(255,122,26,0.1)]"
                  >
                    <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-kinetic/30 bg-kinetic/10 text-kinetic">
                      <c.icon className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="font-hud text-[10px] uppercase tracking-[0.24em] text-[#6B7280]">{c.label}</p>
                      <p className="mt-1 font-display text-lg font-bold group-hover:text-kinetic transition-colors">{c.value}</p>
                    </div>
                  </a>
                </Reveal>
              ))}
              <Reveal delay={0.3}>
                <div className="rounded-2xl border border-kinetic/25 bg-kinetic/5 p-6" data-testid="contact-response-note">
                  <Eyebrow>PREFER TO TALK?</Eyebrow>
                  <p className="mt-3 text-sm text-[#9CA3AF] leading-relaxed">
                    Call or WhatsApp us directly for mission quotes, fleet demos and batch enrolments —
                    the fastest way to reach an Aeris operator.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
